import { connect } from 'react-redux';
import { IStoreState } from 'store';
import GeneralInformation from 'components/deal-results/general-information/general-information';
import { convertNumberToSimpleCurrency, formatNumber } from 'lib/helpers/currency';
import { EEvaluationUnit, ISummaryForecast, TNullableFloat } from 'store/deal-evaluation/types';
import labels from 'constants/labels';
import round from 'lib/helpers/round';
import { mapNameToDisplayName } from 'lib/helpers/field-options';
import { generateContractPeriodString } from 'lib/helpers/date-range';
import { capitalizeFirstLetter } from 'lib/helpers/string-manipulators';
import { getDealStatusIndicatorColor } from 'components/deal-results/general-information/selectors';
import { IFieldOption } from 'store/field-options/types';

export interface IGeneralInformationProps {
  headlineSize: string;
  volume: string;
  upliftOverBatna: string;
  pricePerUnitLabel: string;
  contractPriceTotal: string;
  contractRevenueTotal: string;
  netbackPriceTotal: string;
  netbackRevenueTotal: string;
  marginPriceTotal: string;
  marginRevenueTotal: string;
  marginAtRiskPriceTotal: string;
  marginAtRiskRevenueTotal: string;
  costToServeYield: string;
  volumeLabel: string;
  region: string;
  productLine: string;
  dealName: string;
  customerName: string;
  period: string;
  dealStatusIndicatorColor: string;
  dealStatusText: string;
}
function evaluateVolume(volume: TNullableFloat) {
  return volume ?? 0;
}

function evaluateHeadline(headline: TNullableFloat) {
  return headline ?? 0;
}

function evaluateUplift(upliftOverBatna: TNullableFloat) {
  return upliftOverBatna ?? 0;
}

function evaluateVolumnLabel(selectedUnitType: EEvaluationUnit) {
  return selectedUnitType === EEvaluationUnit.LBS ? labels.volume.pound : labels.volume.ton;
}

function getAve(component: ISummaryForecast) {
  return component.ave ?? 0;
}

function validateProductLine(productLines: IFieldOption[] | undefined, regions: IFieldOption[] | undefined) {
  return productLines && regions;
}

function getCustomer(customer: string | undefined) {
  return customer ?? '-';
}

function getDeal(dealName: string) {
  return dealName ?? '-';
}

function getPricePerUnit(selectedUnitType: EEvaluationUnit) {
  return selectedUnitType === EEvaluationUnit.TON ? labels.currency.dollarsPerTon : labels.currency.centsPerPound;
}
const UNIT_TYPE = 0.01;
function getCurrencyMultiplier(selectedUnitType: EEvaluationUnit) {
  return selectedUnitType === EEvaluationUnit.LBS ? UNIT_TYPE : 1;
}

function processNetBack(netback: ISummaryForecast, propsToReturn: IGeneralInformationProps, priceToRevenue: number) {
  const processNetBackAssign = propsToReturn;
  if (netback) {
    const netbackValue = getAve(netback);
    processNetBackAssign.netbackPriceTotal = formatNumber(netbackValue);
    processNetBackAssign.netbackRevenueTotal = formatNumber(round(netbackValue * priceToRevenue), 0);
  } else {
    processNetBackAssign.netbackPriceTotal = 'N/A';
    processNetBackAssign.netbackRevenueTotal = 'N/A';
  }
}

function processSales(sales: ISummaryForecast, propsToReturn: IGeneralInformationProps, priceToRevenue: number) {
  const processSalesAssign = propsToReturn;
  if (sales) {
    const salesValue = getAve(sales);
    processSalesAssign.contractPriceTotal = formatNumber(salesValue);
    processSalesAssign.contractRevenueTotal = formatNumber(round(salesValue * priceToRevenue), 0);
  } else {
    processSalesAssign.contractPriceTotal = 'N/A';
    processSalesAssign.contractRevenueTotal = 'N/A';
  }
}

function processMargin(
  margin: ISummaryForecast | null,
  propsToReturn: IGeneralInformationProps,
  priceToRevenue: number
) {
  const processMarginAssign = propsToReturn;
  if (margin) {
    const marginValue = getAve(margin);
    processMarginAssign.marginPriceTotal = formatNumber(marginValue);
    processMarginAssign.marginRevenueTotal = formatNumber(round(marginValue * priceToRevenue), 0);
  } else {
    processMarginAssign.marginPriceTotal = 'N/A';
    processMarginAssign.marginRevenueTotal = 'N/A';
  }
}

function processMarginAtRisk(
  marginAtRisk: TNullableFloat,
  propsToReturn: IGeneralInformationProps,
  priceToRevenue: number
) {
  const processMarginAtRiskAssign = propsToReturn;
  if (marginAtRisk) {
    processMarginAtRiskAssign.marginAtRiskPriceTotal = formatNumber(marginAtRisk);
    processMarginAtRiskAssign.marginAtRiskRevenueTotal = formatNumber(round(marginAtRisk * priceToRevenue), 0);
  } else {
    processMarginAtRiskAssign.marginAtRiskPriceTotal = 'N/A';
    processMarginAtRiskAssign.marginAtRiskRevenueTotal = 'N/A';
  }
}

function processCostToServe(costToServeYield: TNullableFloat, propsToReturn: IGeneralInformationProps) {
  const processCostToServeAssign = propsToReturn;
  if (costToServeYield) {
    processCostToServeAssign.costToServeYield = formatNumber(costToServeYield);
  } else {
    processCostToServeAssign.costToServeYield = 'N/A';
  }
}

const mapStateToProps = (state: IStoreState): IGeneralInformationProps => {
  const { dealEdit, dealEvaluation, fieldOptions } = state;
  const { name: dealName, customer, region: regionRaw, productLine: productLineRaw, products, status } = dealEdit;
  const { productLines, regions } = fieldOptions;
  const { loading, selectedForecastType, selectedUnitType } = dealEvaluation;

  const data = dealEvaluation[selectedForecastType];
  let region = '-';
  let productLine = '-';
  if (validateProductLine(productLines, regions)) {
    region = mapNameToDisplayName(regionRaw, regions);
    productLine = mapNameToDisplayName(productLineRaw, productLines);
  }

  const propsToReturn: IGeneralInformationProps = {
    region,
    productLine,
    customerName: getCustomer(customer),
    dealName: getDeal(dealName),
    period: generateContractPeriodString(products),
    headlineSize: '-',
    volume: '-',
    upliftOverBatna: '-',
    pricePerUnitLabel: getPricePerUnit(selectedUnitType),
    contractPriceTotal: '-',
    contractRevenueTotal: '-',
    netbackPriceTotal: '-',
    netbackRevenueTotal: '-',
    marginPriceTotal: '-',
    marginRevenueTotal: '-',
    marginAtRiskPriceTotal: '-',
    marginAtRiskRevenueTotal: '-',
    costToServeYield: '-',
    volumeLabel: '',
    dealStatusIndicatorColor: getDealStatusIndicatorColor(state, status),
    dealStatusText: capitalizeFirstLetter(status),
  };

  if (!data || !products) {
    return propsToReturn;
  }

  const { sales, headline, upliftOverBatna, netback, volume, margin, marginAtRisk, yield: costToServeYield } = data;

  const currencyMultiplier = getCurrencyMultiplier(selectedUnitType);

  if (loading) {
    propsToReturn.headlineSize = labels.general.ellipsis;
    propsToReturn.volume = labels.general.ellipsis;
    propsToReturn.upliftOverBatna = labels.general.ellipsis;
    propsToReturn.contractPriceTotal = labels.general.ellipsis;
    propsToReturn.contractRevenueTotal = labels.general.ellipsis;
    propsToReturn.netbackPriceTotal = labels.general.ellipsis;
    propsToReturn.netbackRevenueTotal = labels.general.ellipsis;
    propsToReturn.marginPriceTotal = labels.general.ellipsis;
    propsToReturn.marginRevenueTotal = labels.general.ellipsis;
    propsToReturn.marginAtRiskPriceTotal = labels.general.ellipsis;
    propsToReturn.marginAtRiskRevenueTotal = labels.general.ellipsis;
    propsToReturn.costToServeYield = labels.general.ellipsis;
  } else {
    const totalVolume = evaluateVolume(volume);
    const priceToRevenue = totalVolume * currencyMultiplier;
    const headlineSizeNumber = round(evaluateHeadline(headline));
    const revenueUpliftOverBatna = round(evaluateUplift(upliftOverBatna) * currencyMultiplier);

    // general
    propsToReturn.volume = formatNumber(totalVolume, 0);
    propsToReturn.headlineSize = convertNumberToSimpleCurrency(headlineSizeNumber);
    propsToReturn.upliftOverBatna = convertNumberToSimpleCurrency(revenueUpliftOverBatna);
    propsToReturn.volumeLabel = evaluateVolumnLabel(selectedUnitType);

    // sales
    processSales(sales, propsToReturn, priceToRevenue);

    // netback
    processNetBack(netback, propsToReturn, priceToRevenue);

    // margin
    processMargin(margin, propsToReturn, priceToRevenue);

    // margin at risk
    processMarginAtRisk(marginAtRisk, propsToReturn, priceToRevenue);

    // cost to serve yield
    processCostToServe(costToServeYield, propsToReturn);
  }

  return propsToReturn;
};

export default connect(mapStateToProps)(GeneralInformation);
